import { FC, Fragment, useMemo } from 'react';
import { capitalize, Typography } from '@mui/material';
import {
    AccountBox as AccountBoxIcon,
    AttachMoney as AttachMoneyIcon,
    Flag as FlagIcon,
    GolfCourse as GolfCourseIcon,
    Group as GroupIcon,
    LocalAtm as LocalAtmIcon,
    LocalOffer as LocalOfferIcon,
    MonitorHeartOutlined as MonitorHeartOutlinedIcon,
} from '@mui/icons-material';

import { Link } from '../../../../../components/Link';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';
import Column from '../../../../../components/Column';
import OverviewList, { OverviewListItem } from '../../../../../components/List/OverviewList';
import Utils from '../../../../../components/Utils';

import { Campaign, Platform } from '../../../../../types/Campaign';
import { useCampaignPageContext } from '../../../../../hooks/Contexts/Pages/CampaignPageContext';
import AssignDspCampaignButton from '../Button/AssignDspCampaignButton';
import DspCampaignIdValue from '../Value/DspCampaignIdValue';
import DspCampaignLink from '../Link/DspCampaignLink';
import CampaignOemProgramValue from '../../../../../features/Campaign/Values/CampaignOemProgramValue';
import { hasAccess } from '../../../../../components/AccessGuard';
import { USER_GROUP_ADMIN } from '../../../../../types/User';

interface CampaignOverviewListProps {
    campaign: Campaign;
}

const CampaignOverviewList: FC<CampaignOverviewListProps> = ({ campaign }) => {
    const { userContext } = useUserContext();
    const { setCampaign } = useCampaignPageContext();

    const canAccessOemProgram = hasAccess(userContext, `${USER_GROUP_ADMIN}`);

    const items: OverviewListItem[] = useMemo(() => {
        return [
            {
                adminOnly: true,
                icon: AccountBoxIcon,
                primary: campaign.platform === Platform.AMAZON ? 'Amazon DSP' : 'DV360',
                secondary: (
                    <Column gap={0} alignItems="flex-end">
                        {campaign?.dspCampaignId ? (
                            <Fragment>
                                <DspCampaignLink
                                    dspEntityId={campaign?.dspEntity?.entityId as string}
                                    dspCampaignId={campaign.dspCampaignId as string}
                                    platform={campaign.platform}
                                    dv360AdvertiserId={campaign.dealer?.dv360Advertiser}
                                />

                                <DspCampaignIdValue
                                    value={campaign.dspCampaignId as string}
                                    campaignId={campaign.id}
                                    platform={campaign.platform}
                                />
                            </Fragment>
                        ) : (
                            <Fragment>
                                {campaign.deliveryStatusId.startsWith('PENDING') ? (
                                    <AssignDspCampaignButton
                                        campaign={campaign}
                                        onChange={(next: Campaign) => {
                                            setCampaign((prev: Campaign) => ({ ...prev, ...next }));
                                        }}
                                    />
                                ) : (
                                    '-'
                                )}
                            </Fragment>
                        )}
                    </Column>
                ),
            },

            {
                adminOnly: true,
                icon: GroupIcon,
                primary: 'Agency Name',
                secondary: campaign?.dealer?.agency?.name ? (
                    <Link href={`/agencies/${campaign.dealer.agencyId}`} size="small">
                        {campaign?.dealer?.agency?.name}
                    </Link>
                ) : (
                    ' - '
                ),
            },
            ...(!userContext.isAdvertiser()
                ? [
                      {
                          icon: AccountBoxIcon,
                          primary: 'Advertiser Name',
                          secondary: campaign?.dealer?.dealerName ? (
                              <Link href={`/advertisers/${campaign.dealerId}`} size="small">
                                  {campaign?.dealer?.dealerName}
                              </Link>
                          ) : (
                              ' - '
                          ),
                      },
                  ]
                : []),
            {
                icon: GolfCourseIcon,
                primary: 'Campaign Strategy',
                secondary: <Typography variant="body2">{campaign.strategyType || ' - '}</Typography>,
            },
            {
                icon: FlagIcon,
                primary: 'Campaign Goal',
                secondary: <Typography variant="body2">{campaign.goal || ' - '}</Typography>,
            },
            {
                icon: LocalAtmIcon,
                primary: 'Budget Cap',
                secondary: (
                    <Typography variant="body2">
                        {capitalize((campaign?.budgetCapType?.toLowerCase() as string) || ' - ')}
                    </Typography>
                ),
            },
            {
                adminOnly: true,
                icon: AttachMoneyIcon,
                primary: 'Agency Fees',
                secondary: (
                    <Typography variant="body2">
                        {campaign?.campaignFee?.agencyFee
                            ? `${Utils.formatPercent(campaign?.campaignFee?.agencyFee)}%`
                            : ' - '}
                    </Typography>
                ),
            },
            {
                icon: MonitorHeartOutlinedIcon,
                primary: 'Exposure Frequency',
                secondary: (
                    <Typography variant="body2">
                        {capitalize(
                            ((campaign.frequencyType === 'UNCAPPED'
                                ? 'Use default uncapped frequency'
                                : campaign?.frequencyType?.toLocaleLowerCase()) as string) || ' - '
                        )}
                    </Typography>
                ),
            },
            ...(canAccessOemProgram
                ? [
                      {
                          adminOnly: true,
                          icon: LocalOfferIcon,
                          primary: 'OEM Program',
                          secondary: <CampaignOemProgramValue campaign={campaign} />,
                      },
                  ]
                : []),
        ];
    }, [userContext, setCampaign, campaign]);

    return <OverviewList items={items} sx={{ p: 0 }} />;
};

export type { CampaignOverviewListProps };
export default CampaignOverviewList;
